import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

export class StringForm extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "form";
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);

    object.getProperty<Array<any>>("form-events")?.forEach((event) => {
      event.eventElement.addEventListener("beforeinput", event.eventCallback);
      event.eventElement.removeEventListener(event.eventType, event.eventCallback);
    });
    object.setProperty<Array<any>>("form-events", []);

    super.onObjectConnected(object);
    const form = object.htmlElement as HTMLFormElement;

    const serviceTypes = ["error", "group"];

    const isServiceAttr = (attr: string) =>
      serviceTypes.some((type) => attr.startsWith(type + "["));

    const fieldsList = Array.from(form.querySelectorAll("[string-input]")).filter(
      (el) => !isServiceAttr(el.getAttribute("string-input") || "")
    ) as Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;

    const fields: Record<string, any> = {};
    for (let idx = 0; idx < fieldsList.length; idx++) {
      const field = fieldsList[idx];
      const key = this.getInputKey(field, idx);

      const ruleString =
        this.tools.domAttribute.process({
          element: field,
          key: "input",
        }) ?? "";

      fields[key] = this.tools.ruleParser.process({ value: ruleString });

      const eventType =
        field instanceof HTMLSelectElement ||
        (field instanceof HTMLInputElement && (field.type === "checkbox" || field.type === "radio"))
          ? "change"
          : "input";

      const callback = () => {
        if (!field.classList.contains("-error")) {
          return;
        }
        const key = this.getInputKey(field, idx);
        const rules = fields[key];
        const value = this.getFieldValue(field);

        const { valid, errors } = this.tools.validation.process({
          rules,
          value,
        });

        const errorBlock = form.querySelector(`[string-input="error[${key}]"]`);
        const groupBlock = form.querySelector(`[string-input="group[${key}]"]`);
        if (errorBlock) {
          errorBlock.innerHTML = "";
          for (const errorMsg of errors) {
            const span = document.createElement("span");
            span.textContent = errorMsg;
            errorBlock.appendChild(span);
          }
        }

        field.classList.toggle("-error", !valid);
        field.classList.toggle("-valid", valid);
        if (groupBlock) {
          groupBlock.classList.toggle("-error", !valid);
          groupBlock.classList.toggle("-valid", valid);
        }
      };
      const callbackBeforeInput = (e: any) => {
        const key = this.getInputKey(field, idx);
        const rules = fields[key];
        const val = field.value;

        const start = (field as HTMLInputElement | HTMLTextAreaElement).selectionStart ?? 0;
        const end = (field as HTMLInputElement | HTMLTextAreaElement).selectionEnd ?? 0;
        let nextVal = val;

        switch (e.inputType) {
          case "deleteContentBackward":
            nextVal =
              start === end && start > 0
                ? val.slice(0, start - 1) + val.slice(end)
                : val.slice(0, start) + val.slice(end);
            break;
          case "deleteContentForward":
            nextVal =
              start === end && start < val.length
                ? val.slice(0, start) + val.slice(start + 1)
                : val.slice(0, start) + val.slice(end);
            break;
          case "insertFromPaste":
          case "insertFromDrop":
          case "insertReplacementText":
            nextVal = val.slice(0, start) + (e.data || "") + val.slice(end);
            break;
          default:
            if (typeof e.data === "string") {
              nextVal = val.slice(0, start) + e.data + val.slice(end);
            }
        }

        const { errors } = this.tools.validation.process({
          rules,
          value: nextVal,
          type: "beforeinput",
        });

        if (errors.length > 0) e.preventDefault();
      };
      object.getProperty<Array<any>>("form-events").push({
        eventElement: field,
        eventType: eventType,
        eventCallback: callback,
      });

      object.getProperty<Array<any>>("form-events").push({
        eventElement: field,
        eventType: "beforeinput",
        eventCallback: callbackBeforeInput,
      });
      field.addEventListener("beforeinput", callbackBeforeInput);

      field.addEventListener(eventType, callback);
      field.classList.add("-inited");
    }

    const submitCallback = (e: any) => {
      e.preventDefault();
      let allValid = true;
      const data: Record<string, any> = {};

      for (let idx = 0; idx < fieldsList.length; idx++) {
        const field = fieldsList[idx];
        if (field.disabled) continue;
        if (field instanceof HTMLInputElement && field.type === "hidden") continue;

        const key = this.getInputKey(field, idx);

        const rules = fields[key];
        const value = this.getFieldValue(field);
        data[key] = value;

        const { valid, errors } = this.tools.validation.process({
          rules,
          value,
        });

        const errorBlock = form.querySelector(`[string-input="error[${key}]"]`);
        const groupBlock = form.querySelector(`[string-input="group[${key}]"]`);
        if (errorBlock) {
          errorBlock.innerHTML = "";
          for (const errorMsg of errors) {
            const span = document.createElement("span");
            span.textContent = errorMsg;
            errorBlock.appendChild(span);
          }
        }

        field.classList.toggle("-error", !valid);
        field.classList.toggle("-valid", valid);

        if (groupBlock) {
          groupBlock.classList.toggle("-error", !valid);
          groupBlock.classList.toggle("-valid", valid);
        }

        if (!valid) {
          allValid = false;
        }
      }

      if (allValid) {
        this.events.emit(`form:submit:${object.id}`, data);
      } else {
        const firstInvalid = fieldsList.find((field, idx) => {
          const key = this.getInputKey(field, idx);
          const rules = fields[key];
          const value = this.getFieldValue(field);
          const { valid } = this.tools.validation.process({ rules, value });
          return !valid;
        });
        if (firstInvalid && typeof firstInvalid.focus === "function") {
          firstInvalid.focus();
        }
        this.events.emit(`form:invalid:${object.id}`);
      }
    };

    object.getProperty<Array<any>>("form-events").push({
      eventElement: form,
      eventType: "submit",
      eventCallback: submitCallback,
    });

    form.addEventListener("submit", submitCallback);
  }

  onObjectConnected(object: StringObject) {}

  getInputKey(
    field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    idx: number
  ): string {
    return (
      this.tools.domAttribute.process({
        element: field,
        key: "id",
      }) ||
      field.getAttribute("name") ||
      field.getAttribute("id") ||
      `input-${idx}`
    );
  }

  getFieldValue(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): any {
    if (field instanceof HTMLInputElement) {
      if (field.type === "checkbox") {
        if (field.name) {
          const form = field.form || field.closest("form");
          const checked = form
            ? Array.from(
                form.querySelectorAll<HTMLInputElement>(
                  `input[type="checkbox"][name="${field.name}"]:checked`
                )
              )
            : [field];
          if (checked.length > 1) return checked.map((el) => el.value);
          return checked.length === 1 ? checked[0].value : "";
        }
        return field.checked;
      }
      if (field.type === "radio") {
        if (field.name) {
          const form = field.form || field.closest("form");
          const checked = form?.querySelector<HTMLInputElement>(
            `input[type="radio"][name="${field.name}"]:checked`
          );
          return checked ? checked.value : "";
        }
        return field.checked ? field.value : "";
      }
      return field.value;
    }
    if (field instanceof HTMLSelectElement) {
      if (field.multiple) {
        return Array.from(field.selectedOptions).map((opt) => opt.value);
      }
      return field.value;
    }
    if (field instanceof HTMLTextAreaElement) {
      return field.value;
    }
    return "";
  }
}
